import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OrganizationUnitItem } from '../../models/filter-data.model';
import { UpsertUserRoleAssignmentDto, UserRole, UserRoleAssignmentDto } from '../../services/user.service';

export interface UserRoleAssignmentDialogData {
  assignment?: UserRoleAssignmentDto;
}

export interface UserRoleAssignmentDialogResult {
  request: UpsertUserRoleAssignmentDto;
  organizationUnitName: string | null;
  organizationUnitLevelId: number | null;
}

@Component({
  selector: 'app-user-role-assignment-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    ModalShellComponent,
    UnitSelectorComponent,
  ],
  template: `
    <app-modal-shell [config]="modalConfig">
      <div modal-content [formGroup]="form" class="assignment-dialog-content">
        <mat-form-field class="full-width">
          <mat-label>Rol</mat-label>
          <mat-select formControlName="role">
            <mat-option *ngFor="let role of roleOptions" [value]="role.value">
              {{ role.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <app-unit-selector
          class="full-width"
          label="Unidad"
          [required]="true"
          [unitSource]="'all'"
          [groupByLevel]="true"
          groupByMode="level"
          [showClearButton]="true"
          formControlName="organizationUnitId"
          (unitChange)="onUnitSelected($event)">
        </app-unit-selector>

        <div class="assignment-scope" *ngIf="shouldShowLowerLevelUnitsNote">
          <mat-icon>account_tree</mat-icon>
          <span>Incluye unidades de nivel inferior</span>
        </div>
      </div>

      <ng-container modal-actions>
        <button mat-button type="button" (click)="cancel()">Cancelar</button>
        <button mat-flat-button color="primary" type="button" (click)="save()" [disabled]="form.invalid">
          <mat-icon>save</mat-icon>
          Guardar
        </button>
      </ng-container>
    </app-modal-shell>
  `,
  styles: [`
    .assignment-dialog-content {
      display: grid;
      gap: 12px;
      width: min(560px, 76vw);
      padding-top: 16px;
    }

    .full-width {
      width: 100%;
    }

    .assignment-dialog-content .full-width:first-child {
      margin-top: 6px;
    }

    .assignment-scope {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #356399;
      font-size: 13px;
    }

    .assignment-scope mat-icon {
      color: #4a79ad;
      font-size: 18px;
      height: 18px;
      width: 18px;
    }
  `]
})
export class UserRoleAssignmentDialogComponent {
  readonly roleOptions: Array<{ value: UserRole; label: string }> = [
    { value: 'UnitManager', label: 'Responsable de unidad' },
    { value: 'Servant', label: 'Siervo' },
    { value: 'MinisterialManager', label: 'Gestión ministerial regional' },
  ];

  readonly modalConfig: ModalShellConfig;
  readonly form: FormGroup;
  private selectedUnitName: string | null;
  private selectedUnitLevelId: number | null;
  private readonly communityLevelId = 5;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<UserRoleAssignmentDialogComponent, UserRoleAssignmentDialogResult | null>,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) readonly data: UserRoleAssignmentDialogData,
  ) {
    const assignment = data.assignment;
    this.modalConfig = { title: assignment ? 'Editar asignación' : 'Agregar asignación', size: 'lg', layout: 'form' };
    this.selectedUnitName = assignment?.organizationUnitName ?? null;
    this.selectedUnitLevelId = assignment?.organizationUnitLevelId ?? null;

    this.form = this.fb.group({
      role: [assignment?.role ?? 'UnitManager', Validators.required],
      organizationUnitId: [assignment?.organizationUnitId ?? null, Validators.required],
    });
  }

  get shouldShowLowerLevelUnitsNote(): boolean {
    return typeof this.selectedUnitLevelId === 'number'
      && this.selectedUnitLevelId > 0
      && this.selectedUnitLevelId < this.communityLevelId;
  }

  onUnitSelected(unit: OrganizationUnitItem | null): void {
    this.selectedUnitName = unit?.name ?? null;
    this.selectedUnitLevelId = unit?.levelId ?? null;
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      request: {
        role: this.form.get('role')!.value,
        organizationUnitId: this.form.get('organizationUnitId')!.value,
        includeDescendants: true,
        validFrom: null,
        validTo: null,
      },
      organizationUnitName: this.selectedUnitName,
      organizationUnitLevelId: this.selectedUnitLevelId,
    });
  }
}
